import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

/**
 * Bulk-import questions for a single course.
 *
 * Flow:
 *   1. User downloads a per-course template (backend emits xlsx with only the
 *      attribute columns enabled for this tenant).
 *   2. User fills in rows and uploads.
 *   3. Backend returns {total, imported, skipped, discrepancies[]} — we render
 *      the skipped rows inline so users can fix and re-upload if needed.
 */
function ImportQuestionsModal({ tenantId, course, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const res = await api.get(
        `/question-bank/template/${tenantId}/${course.CourseID}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeCode = (course.CourseCode || 'course').replace(/[^A-Za-z0-9_-]+/g, '_');
      a.href = url;
      a.download = `QuestionBank_Template_${safeCode}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    // Friendly guard — server also enforces size and type.
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File is larger than 10 MB');
      return;
    }
    const ok = /\.xlsx?$/i.test(f.name);
    if (!ok) {
      toast.error('Please upload an .xlsx file');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please choose a file to upload');
      return;
    }
    try {
      setUploading(true);
      const form = new FormData();
      form.append('file', file);
      form.append('tenantId', String(tenantId));
      const res = await api.post(
        `/question-bank/import/${course.CourseID}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        setResult(res.data.data);
        if (res.data.data.imported > 0) {
          toast.success(`${res.data.data.imported} question(s) imported`);
          onImported && onImported();
        }
        if (res.data.data.skipped > 0 && res.data.data.imported === 0) {
          toast.warning('No rows were imported — see discrepancies below');
        }
      } else {
        toast.error(res.data.message || 'Import failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content qb-import-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 style={{ margin: 0 }}>Import Questions</h2>
            <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 2 }}>
              {course?.CourseCode} · {course?.CourseName}
            </div>
          </div>
          <button className="btn btn-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Step 1 — download */}
          <div className="qb-import-step">
            <div className="qb-import-step-head">
              <span className="qb-import-step-num">1</span>
              <div>
                <div className="qb-import-step-title">Download the template</div>
                <div className="qb-import-step-hint">
                  The spreadsheet shows only the attribute columns enabled for
                  your tenant (see QP Settings → Question Bank Settings). Keep
                  the header row as-is and add one question per row.
                </div>
              </div>
            </div>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadTemplate}
              disabled={downloading}
              style={{ marginLeft: 36 }}
            >
              {downloading ? 'Preparing…' : '⬇ Download Template (.xlsx)'}
            </button>
          </div>

          {/* Step 2 — upload */}
          <div className="qb-import-step">
            <div className="qb-import-step-head">
              <span className="qb-import-step-num">2</span>
              <div>
                <div className="qb-import-step-title">Upload your file</div>
                <div className="qb-import-step-hint">
                  Each valid row becomes a new question in this course. Rows
                  with missing Question text or invalid Marks are reported back
                  as discrepancies and skipped — the rest still import.
                </div>
              </div>
            </div>
            <div style={{ marginLeft: 36, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                className="qb-import-file-input"
              />
              <button
                className="btn btn-add"
                disabled={!file || uploading}
                onClick={handleUpload}
              >
                {uploading ? 'Uploading…' : 'Upload & Import'}
              </button>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div className="qb-import-result">
              <div className="qb-import-result-summary">
                <div className="qb-import-result-card qb-import-result-ok">
                  <div className="qb-import-result-num">{result.imported}</div>
                  <div className="qb-import-result-label">Imported</div>
                </div>
                <div className="qb-import-result-card qb-import-result-skip">
                  <div className="qb-import-result-num">{result.skipped}</div>
                  <div className="qb-import-result-label">Skipped</div>
                </div>
                <div className="qb-import-result-card">
                  <div className="qb-import-result-num">{result.total}</div>
                  <div className="qb-import-result-label">Total Rows</div>
                </div>
              </div>

              {result.discrepancies?.length > 0 && (
                <div className="qb-import-discrepancies">
                  <div className="qb-import-discrepancies-title">
                    Discrepancies ({result.discrepancies.length})
                  </div>
                  <table className="qb-import-discrepancies-table">
                    <thead>
                      <tr>
                        <th style={{ width: 80 }}>Row #</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.discrepancies.map((d, i) => (
                        <tr key={i}>
                          <td>{d.row}</td>
                          <td>{d.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-cancel" onClick={onClose}>
            {result?.imported > 0 ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImportQuestionsModal;
